import { Component, Inject, OnInit, ViewChild, WritableSignal, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

// models
import { DwUserActivityNames } from '../../../core/models/dwUserActivityNames';
import { DwUserActivityTypes } from '../../../core/models/dwUsersActivityTypes';
import { IConfirmModalAnswer } from '../../../core/modals/confirm-modal/models/confirmModalAnswer';
import { IConfirmModalData } from '../../../core/modals/confirm-modal/models/confirmModalData';

// components
import { ConfirmModalComponent } from '../../../core/modals/confirm-modal/confirm-modal.component';
import { TableComponent } from '../../../core/components/table/table.component';

// services
import { DwUsersActivityService } from '../../../core/services/dw-users-activity.service';
import { ExcelService } from '../../../core/services/xls.service';
import { PlanogramsService } from '../../../core/services/planograms.service';
import { SessionService } from '../../../core/services/session.service';
import { Utils } from '../../../core/services/utils.service';
import { DEFAULTS_PRESETS_SCHEDULED } from '../../../core/components/ph-datepicker/ph-datepicker/preset';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { DownloadService } from '../../../core/services/download.service';
import { DateFormatService } from '../../../core/services/date-format.service';

@Component({
    selector: 'app-execution-modal',
    templateUrl: './execution-modal.component.html',
    styleUrls: ['./execution-modal.component.scss'],
    standalone: false
})
export class ExecutionModalComponent implements OnInit {
	@ViewChild('appTable') appTable: TableComponent;
	formattedPlanograms: any[] = [];
	planograms: any[] = [];
	plans: any[] = [];
	selectedColumns = [
		{ value: 'planogram', checked: false },
		{ value: 'shop_plan', checked: false },
		{ value: 'execution_date', checked: false },
		{ value: 'scheduled_date', checked: false },
		{ value: 'deadline_date', checked: false },
		{ value: 'execute_date', checked: false },
		{ value: 'time_execution', checked: false },
		{ value: 'store_manager', checked: false },
	];
	selectedPlanograms: any[] = [];

	tableData: any = { columns: [], data: [] };
	title: string;
	type: string;

	planogram_id: string | number;
	manager_id: string | number;

	loader: boolean = false;
    isExporting: WritableSignal<boolean> = signal<boolean>(false);
	plan_filter: number = -1;
	search: string = '';
	table_sorting: { index: number, order: string, tableIdx: number };

    constructor(
        private router: Router,
        private dialog: MatDialog,
        private dialogRef: MatDialogRef<ExecutionModalComponent>,
		@Inject(MAT_DIALOG_DATA) public data: any,
        private translate: TranslateService,
		private excelService: ExcelService,
		private planogramsService: PlanogramsService,
        private sessionService: SessionService,
        private dwUsersActivityService: DwUsersActivityService,
        private downloadService: DownloadService,
		private dateFormatService: DateFormatService
    ) {}

	get hasFailedScheduled(): boolean {
		if (this.selectedPlanograms.length === 0) {
			return false;
		}

		return this.selectedPlanograms.every(p => {
			const planogram = this.planograms.find(
				item => item.main_stand_id === p.main_stand_id && item.planogram_id === p.planogram_id
			);
			return planogram?.failed_scheduled_send === true;
		});
	}

	private formatPlanograms(planograms: any[]) {
		return planograms.map(p => ({
			planogram: p.stand_name,
			shop_plan: p.planogram_name,
			last_update_date: this.dateFormatService.formatDate(p.stand_updated_at, 'date'),
			execution_date: this.dateFormatService.formatDate(p.created_at, 'date'),
			execute_date: p.updated_at,
			scheduled_date: p.scheduled_date,
			deadline_date: p.deadline_date,
			time_execution: p.tracked_time,
			enable_time_tracking: p.enable_time_tracking,
			store_manager: p.manager || '',
			main_stand_id: p.main_stand_id,
			planogram_id: p.planogram_id,
			failed_scheduled_send: p.failed_scheduled_send,
			id: p.stand_id
		}));
	}

	ngOnInit() {
		this.planograms = this.data.data || [];
		const ps_shops_set = this.planograms.map(p => p.planogram_id);

		this.plans = [this.data.plans[0], ...this.data.plans.filter(p => ps_shops_set.includes(p.id)).sort(Utils.sortBy('name'))];

		this.title = this.data.title;
		this.type = this.data.type;

		if (this.type === 'done_execution') {
			this.selectedColumns = this.selectedColumns.filter(
					c => c.value !== 'scheduled_date'
			);
		}

		if (this.type == 'send_execution') {
			this.selectedColumns = [
				...this.selectedColumns.slice(0, 2),
				{ value: 'store_manager', checked: false },
				{ value: 'execution_date', checked: false },
				{ value: 'deadline_date', checked: false },
				{ value: 'time_tracker', checked: false },
				{ value: 'last_update_date', checked: false }
			];
		}

		if (this.type == 'scheduled_execution') {
			this.selectedColumns = [
				...this.selectedColumns.slice(0, 2),
				{ value: 'store_manager', checked: false },
				{ value: 'scheduled_date', checked: false },
				{ value: 'deadline_date', checked: false },
				{ value: 'time_tracker', checked: false },
				{ value: 'last_update_date', checked: false }
			];
		}

		this.formattedPlanograms = this.formatPlanograms(this.planograms);
		this.tableData = this.setTable();

		if (this.plan_filter && ~this.plans.findIndex(s => s.id == this.plan_filter)) {
			this.plans.forEach(p => p.active = p.id == this.plan_filter);

			this.filterByPlan(this.plan_filter);
		}
	}

	setTable() {
		const columns = this.selectedColumns.map(c => c.value.toUpperCase()),
			data = this.formattedPlanograms.map(p => {
				const row: any = [];

				this.selectedColumns
					.map(c => c.value)
					.forEach(key => {
						if (key === 'scheduled_date') {
							this.type === 'scheduled_execution' ?
								row.push({
									data: {
										datepicker: true,
										date: p[key],
										min_date: new Date(new Date().setDate(new Date().getDate() + 1)).toString(),
										max_date: p['deadline_date'] ? p['deadline_date'].toString() : null,
										type: 'scheduled',
										placeholder: 'SCHEDULED_PLANOGRAMS.IN_TIME',
										presets: DEFAULTS_PRESETS_SCHEDULED
									}
								}) : row.push({
									data: {
										scheduled_date: p[key]
									}
								});
							return;
						}
						if (key === 'deadline_date') {
							['send_execution', 'scheduled_execution'].includes(this.type)  ?
								row.push({
									data: {
										datepicker: true,
										date: p[key],
										min_date: p['scheduled_date'] ? p['scheduled_date'].toString() : new Date().toString(),
										type: 'deadline'
									}
								}) : row.push({
									data: {
										deadline_date: p[key],
										deadline_overdue: p[key] && Utils.dateDayComparison(new Date(p['execute_date']), new Date(p[key]))
									}
								});
							return;
						}
						if (key === 'time_execution') {
							row.push({
								data: {
									timeTracker: true,
									time: p[key],
									enabled: p['enable_time_tracking']
								}
							});
							return;
						}
						if (key === 'time_tracker') {
							row.push({
								data: {
									toggle: true,
									value: p['enable_time_tracking']
								}
							});
							return;
						}
						if (key === 'execute_date') {
							row.push(this.dateFormatService.formatDate(p[key], 'date'));
							return;
						}
						if (key === 'planogram') {
							row.push({
								data: p[key],
								failed_scheduled_send: p['failed_scheduled_send']
							});
							return;
						}
						row.push(typeof p[key] == 'number' ? Utils.round(p[key]) : p[key])
					});

				row.main_stand_id = p.main_stand_id;
				row.planogram_id = p.planogram_id;
				row.id = p.id;

				return row;
			});

		if (this.table_sorting) {
			data.sort(Utils.sortData(this.table_sorting.index, this.table_sorting.order == 'asc' ? 'desc' : 'asc'));
			this.table_sorting = Object.assign({}, this.table_sorting);
		}

		return { columns, data };
	}

	cancelExecutions() {
		if (!this.selectedPlanograms.length) return;

		const dialog = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
			width: '574px',
			autoFocus: false,
			backdropClass: 'back-drop',
			data: {
				label: 'CANCEL_EXECUTION_MESSAGE',
				confirmLabel: 'OK',
				cancelLabel: 'NO',
				twoAnswers: true
			}
		});

		dialog.afterClosed().subscribe(async (result: IConfirmModalAnswer) => {
			if (!result.answer) return;

			this.loader = true;

			const executions_to_delete = this.selectedPlanograms
				.map(selected => (
					this.planograms.find(p =>
						(p.main_stand_id == selected.main_stand_id && p.planogram_id == selected.planogram_id) ||
						p.stand_name == selected.name
					) || {})
				)
				.map(e => ({ main_stand_id: e.main_stand_id, planogram_id: e.planogram_id }));

			await this.planogramsService.cancelExecutions(executions_to_delete);

			this.selectedPlanograms = [];
			this.appTable.selectedList = [];

			this.loader = false;
		});
	}

	onNoClick(): void {
		this.dialogRef.close(false);
	}

	async apply() {
		if (!this.selectedPlanograms.length) return;

		this.loader = true;

		const requestData = [], shopNames = {};

		this.data.shops.forEach(shop => shopNames[shop.id] = shop.name);

		const planogramsToSend = this.selectedPlanograms.filter(selected => {
			const planogram = this.planograms.find(
				item => item.main_stand_id === selected.main_stand_id && item.planogram_id === selected.planogram_id
			);
			return planogram && !planogram.failed_scheduled_send;
		});

		planogramsToSend.map(selected => {
			let used = requestData.find(s => s.main_stand_id == selected.main_stand_id),
				planogram = this.planograms
					.find(p => p.main_stand_id == selected.main_stand_id && p.planogram_id == selected.planogram_id) || {};

			if (!used) {
				used = {
					main_stand_id: planogram.main_stand_id,
					name: planogram.stand_name,
					shops: []
				};
				requestData.push(used);
			}

			used.shops.push({
				stand_id: planogram.main_stand_id,
				planogram_id: planogram.planogram_id,
				user_id: planogram.manager_id,
				shop_name: shopNames[planogram.shop_id],
				stand_name: planogram.stand_name,
				manager_id: planogram.manager_id,
				manager_name: planogram.manager,
				scheduled_date: planogram.scheduled_date ? this.sessionService.formatDate(planogram.scheduled_date, 'yyyy-MM-dd') : null,
				deadline_date: planogram.deadline_date ? this.sessionService.formatDate(planogram.deadline_date, 'yyyy-MM-dd') : null,
				enable_time_tracking: planogram.enable_time_tracking
			});
		});

		await this.planogramsService.bulkExecutions({
			execution_data: Utils.to1DArray(requestData.map(s => s.shops))
		});

		this.loader = false;
		this.refreshTableData();
		return this.dialogRef.close(true);
	}

	private refreshTableData() {
		this.formattedPlanograms = this.formatPlanograms(this.planograms);
		this.tableData = this.setTable();

		this.selectedPlanograms = this.selectedPlanograms.filter(selected => {
			const planogram = this.planograms.find(
				item => item.main_stand_id === selected.main_stand_id && item.planogram_id === selected.planogram_id
			);
			return planogram?.failed_scheduled_send === true;
		});
	}

    async export() {
        this.isExporting.set(true);
        const data = await this.exportFilter();

        const request$: Observable<void> = this.excelService
            .exportAsExcelFromServer$(data)
            .pipe(finalize(() => this.isExporting.set(false)));

        this.downloadService.excelFilesDownloadSubject.next({
            name: data.name,
            getData$: request$
        });

		this.dwUsersActivityService.track(
			this.type == 'send_execution'
				? DwUserActivityNames.PlanogramsOnExecution
				: DwUserActivityNames.CompletedPlanograms,
			DwUserActivityTypes.DownloadData
		);
	}

	async exportFilter() {
		let columnsExcel,
			excelName,
			excelData = [...this.tableData.data],
			filename;

		const trans = await this.translate.get(['NO_LIMIT_2', 'REQUIRED', 'WITHOUT_TRACKING', ...this.tableData.columns, this.title]).toPromise();

		excelData.forEach(data => {
			if (data[0] && typeof data[0] === 'object' && data[0].data) {
				data[0] = data[0].data;
			}
			const indexDeadlineDate: number = data.findIndex(row => row.data && (row.data.hasOwnProperty('deadline_date') || (row.data.datepicker && row.data.type === 'deadline')));
			const indexDataPicker: number = data.findIndex(row => row.data && row.data.datepicker && row.data.type !== 'deadline');
			const indexToggle: number = data.findIndex(row => row.data && row.data.hasOwnProperty('toggle'));
			const indexTimeTracker: number = data.findIndex(row => row.data && row.data.hasOwnProperty('timeTracker'));

			if (indexDeadlineDate !== -1) {
				const deadlineData = data[indexDeadlineDate].data;
				if (deadlineData.datepicker) {
					data[indexDeadlineDate] = deadlineData.date ?
						this.dateFormatService.formatDate(deadlineData.date, 'date') :
						trans['NO_LIMIT_2'];
				} else {
					data[indexDeadlineDate] = deadlineData.deadline_date ?
						this.dateFormatService.formatDate(deadlineData.deadline_date, 'date') :
						trans['NO_LIMIT_2'];
				}
			}

			if (indexDataPicker !== -1) {
				data[indexDataPicker] = data[indexDataPicker].data.date ?
					this.dateFormatService.formatDate(data[indexDataPicker].data.date, 'date') :
					trans['NO_LIMIT_2'];
			}
			if (indexToggle !== -1) data[indexToggle] = data[indexToggle].data.value ? trans['REQUIRED'] : '-';
			if (indexTimeTracker !== -1) data[indexTimeTracker] = data[indexTimeTracker].data.time || (!data[indexTimeTracker].data.enabled ? trans['WITHOUT_TRACKING'] : '00:00:00');
		});

		if (this.table_sorting) {
			excelData.sort(Utils.sortData(this.table_sorting.index, this.table_sorting.order));
			this.table_sorting = Object.assign({}, this.table_sorting);
		}

		columnsExcel = this.tableData.columns.map(i => trans[i]);
		excelName = trans[this.title];

		filename = excelName;

		return {
			pages: [{
				columns: columnsExcel,
				data: excelData,
				name: excelName
			}],
			name: this.dateFormatService.buildExportFileName(filename)
		};
	}

	async selectRow(data) {
		let url: string = '';

		const execution = this.planograms.find(p =>
			(p.main_stand_id == data.tr.main_stand_id && p.planogram_id == data.tr.planogram_id) ||
			(p.stand_name == data.tr.data[0].value && p.planogram_name == data.tr.data[1].value)
		);

        switch (this.type) {
            case 'done_execution':
                await this.planogramsService.showPhotoReport(execution.id);

				if (this.sessionService.hasAccess('ph-control')) {
					url = this.router.serializeUrl(
						this.router.createUrlTree(
							this.sessionService.getLink(['control']),
							{ queryParams: { planogram: execution.main_stand_id, plan: execution.planogram_id } }
						)
					);
				}

                break;

            case 'send_execution':
				if (this.sessionService.hasAccess('ph-planogram-view')) {
					url = this.router.serializeUrl(
						this.router.createUrlTree(
							this.sessionService.getLink(['stand', 'view', execution.stand_id]),
							{ queryParams: { planogram: execution.planogram_id } }
						)
					);
				}

                break;

			case 'scheduled_execution':
				if (this.sessionService.hasAccess('ph-planogram-view')) {
					url = this.router.serializeUrl(
						this.router.createUrlTree(
							this.sessionService.getLink(['stand', 'view', execution.stand_id]),
							{ queryParams: { planogram: execution.planogram_id } }
						)
					);
				}
				break;
		}

		if (url.length) window.open(url);
	}

	selectHandler(row) {
		this.selectedPlanograms.push(
			row.main_stand_id ?
				{
					main_stand_id: row.main_stand_id,
					planogram_id: row.planogram_id
				} :
				{
					name: row.data[0].value
				}
		);
	}

	onUnSelectHandler(row) {
		this.selectedPlanograms = this.selectedPlanograms.filter(p => {
			if (typeof p == 'object' && p.main_stand_id) {
				if (p.main_stand_id == row.main_stand_id) {
					return p.planogram_id != row.planogram_id;
				}

				if (p.planogram_id == row.planogram_id) {
					return p.main_stand_id != row.main_stand_id;
				}

				return true;
			}
			return p != row.data[0].value;
		});
	}

	selectAllHandler(value) {
		const visiblePlanograms =
			Utils.searchObjects<any>(
				this.planograms.filter(p => !~this.plan_filter ? true : p.planogram_id == this.plan_filter),
				['stand_name', 'manager'],
				this.search,
				this.sessionService.personalSettings.searchAlgorithm
			).map(i => ({ main_stand_id: i.main_stand_id, planogram_id: i.planogram_id }));


		if (value) {
			visiblePlanograms.forEach(p => {
				const exists = this.selectedPlanograms.find(s => s.main_stand_id == p.main_stand_id && s.planogram_id == p.planogram_id);
				if (!exists) this.selectedPlanograms.push(p);
			});
		} else {
			if (visiblePlanograms.length == this.planograms.length) {
				this.selectedPlanograms = [];
			} else {
				this.selectedPlanograms = this.selectedPlanograms.filter(s => {
					return !visiblePlanograms.some(p => p.main_stand_id == s.main_stand_id && p.planogram_id == s.planogram_id);
				});
			}
		}
	}

	searchHandler(value) {
		this.search = value;

		this.formattedPlanograms = this.formatPlanograms(
			Utils.searchObjects(
				this.planograms.filter(p => !~this.plan_filter ? true : p.planogram_id == this.plan_filter),
				['stand_name', 'manager'],
				value,
				this.sessionService.personalSettings.searchAlgorithm
			)
		);
		this.tableData = this.setTable();
	}

	selectDeadline(event) {
		this.changePlanogramField({
			stand_id: event.stand,
			planogram_id: event.planogram,
			value: event.date
		}, 'deadline_date');

		this.tableData = this.setTable();
	}

	selectScheduled(event) {
		this.changePlanogramField({
			stand_id: event.stand,
			planogram_id: event.planogram,
			value: event.date
		}, 'scheduled_date');

		this.tableData = this.setTable();
	}

	changeTimeTracker(event) {
		this.changePlanogramField({
			stand_id: event.stand,
			planogram_id: event.planogram,
			value: event.value
		}, 'enable_time_tracking');
	}

	filterByPlan(value) {
		this.plan_filter = value;
		this.formattedPlanograms = this.formatPlanograms(
			Utils.searchObjects(
				this.planograms.filter(p => !~value ? true : p.planogram_id == value),
				['stand_name', 'manager'],
				this.search,
				this.sessionService.personalSettings.searchAlgorithm
			)
		);
		this.tableData = this.setTable();
	}

	changePlanogramField(data, key) {
		this.formattedPlanograms.forEach(stand => {
			if (stand.id === data.stand_id && stand.planogram_id === data.planogram_id)
				stand[key] = data.value;
		});

		this.planograms.forEach(stand => {
			if (stand.stand_id === data.stand_id && stand.planogram_id === data.planogram_id)
				stand[key] = data.value;
		});
	}
}